import React, { useEffect, useState } from 'react';
import { fetchWbsRowsForGantt } from '../api/gantt.api';
import type { GanttData, GanttWbsRow } from '../types/gantt.type';
import WbsGanttChart from './WbsGanttChart';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

type Props = {
    projectId: string;
};

const NO_WBS_MESSAGE = '아직 WBS가 없습니다. WBS를 먼저 입력하세요.';

function isMissingCurrentWbsError(e: any) {
    const status = e?.response?.status;
    const code = e?.response?.data?.error?.code ?? e?.response?.data?.code;
    const message = e?.response?.data?.error?.message ?? e?.response?.data?.message ?? e?.message;

    return status === 404 || code === 'WBS_CURRENT_NOT_FOUND' || message === 'No current WBS list found.';
}

export default function GanttChartContainer({ projectId }: Props) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<GanttWbsRow[]>([]);
    const [anchorDate, setAnchorDate] = useState<string | null>(null);
    const [emptyMessage, setEmptyMessage] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;

        setLoading(true);
        setError(null);
        setRows([]);
        setAnchorDate(null);
        setEmptyMessage(null);

        (async () => {
            try {
                const r = await fetchWbsRowsForGantt(projectId, { zone, setting });
                if (!mounted) return;
                setAnchorDate(r.anchorDate);

                if (!Array.isArray(r.rows) || r.rows.length === 0) {
                    setRows([]);
                    setEmptyMessage(NO_WBS_MESSAGE);
                    return;
                }

                setRows(r.rows);
            } catch (e: any) {
                if (!mounted) return;

                if (isMissingCurrentWbsError(e)) {
                    setRows([]);
                    setEmptyMessage(NO_WBS_MESSAGE);
                    setError(null);
                } else {
                    setError(e?.message ?? 'Failed to load WBS');
                }
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [projectId, zone, setting]);

    return (
        <div>
            {loading && <div className="mb-3 text-sm text-gray-500">Loading WBS...</div>}
            {!loading && error && <div className="mb-3 text-sm text-rose-600">{error}</div>}

            {!loading && !error && rows.length === 0 && (
                <div className="mt-2 mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    표시할 WBS가 없습니다. WBS 창에서 WBS를 먼저 입력하세요.
                </div>
            )}

            {!error && <WbsGanttChart projectId={projectId} rows={rows} zone={zone} anchorDate={anchorDate ?? undefined} />}
        </div>
    );
}
